import React, { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import landingBackground from '../../assets/backgrounds/landingBackground.avif';
import { CONTENT_WIDTH, GOLDEN_MINOR } from '../../styles/golden';
import { BRAND, frostedSx } from '../../styles/ui';
import SiteHeader, { HomeButton } from './SiteHeader';
import { LegalLinks } from './SiteFooter';

interface AuthLayoutProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  // Buttons in the site header for this step of the flow (defaults to Home)
  headerActions?: ReactNode;
  // Bar at the top of the form panel, e.g. registration steps
  header?: ReactNode;
  // Footer: a link or note on one side, the buttons on the other (stacked on phones)
  footerStart?: ReactNode;
  footerActions?: ReactNode;
  // Desktop-only content shown over the photo, e.g. the terms panel
  aside?: ReactNode;
  children: ReactNode;
}

const panelPaddingX = { xs: 3, sm: 5 };

// Site header on top, then a split screen: the form panel takes the golden minor (38.2%)
// and the photo the major part. On phones the panel is the whole page and scrolls normally.
const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  headerActions,
  header,
  footerStart,
  footerActions,
  aside,
  children
}) => (
  <Box
    sx={{
      minHeight: '100vh',
      '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
      // Desktop: exactly one screen tall; the panel scrolls inside
      height: { md: '100vh' },
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--joan-bg)',
      backgroundImage: {
        xs: `linear-gradient(${BRAND.veilFull}, ${BRAND.veilFull}), url(${landingBackground})`,
        md: `linear-gradient(to right, ${BRAND.veilStrong} ${GOLDEN_MINOR}, ${BRAND.veilSoft} 100%), url(${landingBackground})`
      },
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}
  >
    <SiteHeader actions={headerActions ?? <HomeButton />} />

    <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
      <Box
        component="main"
        sx={{
          width: { xs: '100%', md: '50%', lg: GOLDEN_MINOR },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          ...frostedSx,
          backgroundColor: BRAND.panel,
          color: BRAND.ink,
          borderRight: { md: `1px solid ${BRAND.line}` }
        }}
      >
        {header && (
          <Box sx={{ px: panelPaddingX, py: 3, borderBottom: `1px solid ${BRAND.line}` }}>
            {header}
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: { md: 'auto' }, px: panelPaddingX, py: { xs: 4, md: 5 } }}>
          <Box sx={{ width: '100%', maxWidth: CONTENT_WIDTH.text, mx: 'auto' }}>
            {title && (
              <Typography variant="h4" component="h1" sx={{ color: BRAND.ink, mb: subtitle ? 2 : 4 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                {subtitle}
              </Typography>
            )}

            {children}
            <LegalLinks />
          </Box>
        </Box>

        {(footerStart || footerActions) && (
          <Box
            sx={{
              px: panelPaddingX,
              pt: 3,
              pb: 'calc(21px + env(safe-area-inset-bottom))',
              borderTop: `1px solid ${BRAND.line}`,
              backgroundColor: BRAND.header
            }}
          >
            <Box
              sx={{
                maxWidth: CONTENT_WIDTH.text,
                mx: 'auto',
                display: 'flex',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2
              }}
            >
              <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, color: BRAND.ink }}>{footerStart}</Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  gap: 2,
                  '& > *': { width: { xs: '100%', sm: 'auto' } }
                }}
              >
                {footerActions}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1, minWidth: 0, position: 'relative' }}>
        {aside}
      </Box>
    </Box>
  </Box>
);

export default AuthLayout;
